import { useState } from "react";
import { Link } from "react-router-dom";
import { Info, MapPin, Phone } from "lucide-react";
import Seo, { marinaJsonLd } from "../components/Seo";
import PageHero from "../components/PageHero";
import FleetStrip from "../components/FleetStrip";
import WeatherBanner from "../components/WeatherBanner";
import { todayISO } from "../components/BookingWidget";
import { Section, SectionHeading, InfoCard } from "../components/Section";
import Reveal from "../components/Reveal";
import { FLEET_TYPES, LOCATIONS, directionsUrl } from "../lib/content";
import { useHours } from "../hooks/useHours";

const LM = LOCATIONS["lake-milton"];

export default function LakeMilton() {
  const { hoursFor, locations } = useHours();
  const h = hoursFor("lake-milton");
  // The registry decides. Flipping `enabled` on location 9 is the only change
  // needed to turn this page into a live booking page -- no rewrite.
  const live = Boolean((locations || []).find((l) => l.slug === "lake-milton")?.enabled);
  const [date, setDate] = useState(todayISO());

  return (
    <>
      <Seo
        title="Lake Milton State Park Boat Rentals | Call (330) 654-4991"
        description="Boat rentals at Lake Milton State Park, Ohio. Pontoons, jet skis, kayaks and fishing boats. Reservations are taken by phone at (330) 654-4991."
        path="/boat-rentals/lake-milton"
        image="/images/marina_docks.jpeg"
        jsonLd={marinaJsonLd(LM)}
      />

      <PageHero
        image="/images/marina_docks.jpeg"
        alt="Boats moored along the marina docks"
        eyebrow="Lake Milton State Park"
        title="Lake Milton boat rentals"
        subtitle="The same fleet standard and the same rental rules as West Branch — reservations for this lake are handled by phone."
      >
        <a
          href={LM.phoneHref}
          data-testid="milton-hero-call-button"
          className="inline-flex items-center gap-3 rounded-full bg-ember px-7 py-4 font-bold text-white transition-all hover:bg-ember-hover hover:-translate-y-0.5"
        >
          <Phone className="h-5 w-5" aria-hidden="true" />
          Call {LM.phone} to reserve
        </a>
      </PageHero>

      <Section tone="white">
        {live ? (
          <Reveal
            data-testid="milton-live-booking-notice"
            className="flex flex-col gap-4 rounded-2xl border border-lake/30 bg-lake/[0.06] p-6 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="flex gap-3">
              <Info className="mt-0.5 h-5 w-5 shrink-0 text-lake" aria-hidden="true" />
              <div>
                <h2 className="font-display text-lg font-semibold text-navy">Online booking is live at Lake Milton</h2>
                <p className="mt-1 text-sm leading-relaxed text-slate-700">
                  Pick a date below to see every boat that can leave this dock, or call us and we'll do it for you.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <label htmlFor="lm-date" className="sr-only">
                Rental date
              </label>
              <input
                id="lm-date"
                type="date"
                data-testid="milton-date-input"
                value={date}
                min={todayISO()}
                onChange={(e) => e.target.value && setDate(e.target.value)}
                className="h-11 rounded-xl border border-sand-border bg-white px-3 text-sm font-semibold text-navy"
              />
            </div>
          </Reveal>
        ) : (
          <Reveal
            data-testid="milton-phone-notice"
            className="flex flex-col gap-4 rounded-2xl border border-lake/30 bg-lake/[0.06] p-6 sm:flex-row sm:items-center sm:justify-between"
          >
          <div className="flex gap-3">
            <Info className="mt-0.5 h-5 w-5 shrink-0 text-lake" aria-hidden="true" />
            <div>
              <h2 className="font-display text-lg font-semibold text-navy">Online booking is coming to Lake Milton</h2>
              <p className="mt-1 text-sm leading-relaxed text-slate-700">
                We're bringing this location onto the same live booking system West Branch uses. Until then, call us and
                we'll reserve your boat over the phone.
              </p>
            </div>
          </div>
          <a
            href={LM.phoneHref}
            data-testid="milton-notice-call-link"
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-navy px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-navy-surface"
          >
            <Phone className="h-4 w-4" aria-hidden="true" />
            {LM.phone}
          </a>
        </Reveal>
        )}
      </Section>

      {live && (
        <FleetStrip
          locationId={LM.locationId}
          slug={LM.slug}
          date={date}
          showLocationPicker={false}
          heading={`Boats available on ${date}`}
          intro="Each card is a real boat at Lake Milton. Durations, departure times and add-ons are chosen on the boat's page."
        />
      )}

      <Section>
        <SectionHeading
          eyebrow="What's offered"
          title="The same fleet you'll find at West Branch"
          intro="Both of our rental operations run the same way, with the same categories of boats and the same rental requirements."
        />
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {FLEET_TYPES.map((t, i) => (
            <Reveal
              key={t.name}
              delay={i * 60}
              data-testid={`milton-fleet-type-${i}`}
              className="overflow-hidden rounded-2xl border border-sand-border bg-white"
            >
              <img
                src={t.image}
                alt={t.alt}
                loading="lazy"
                width={1264}
                height={848}
                className="w-full object-cover"
                style={{ aspectRatio: "16 / 9" }}
              />
              <div className="p-6">
                <h3 className="font-display text-lg font-semibold text-navy">{t.name}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{t.detail}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section tone="white">
        <SectionHeading eyebrow="Getting here" title="Location & hours" />
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <InfoCard icon={MapPin} title="Where to find us" testId="milton-card-location">
            <span data-testid="milton-address">
              {LM.streetAddress}
              <br />
              {LM.city}, {LM.region} {LM.postalCode}
            </span>
            <a
              href={directionsUrl(LM)}
              target="_blank"
              rel="noreferrer"
              data-testid="milton-directions-link"
              className="mt-2 inline-block font-bold text-lake hover:underline"
            >
              Get directions
            </a>
            <span className="mt-2 block text-xs text-slate-500">
              Inside {LM.park}, managed by the Ohio Department of Natural Resources.
            </span>
          </InfoCard>
          <InfoCard icon={Phone} title="Reservations & questions" testId="milton-card-phone">
            <a href={LM.phoneHref} data-testid="milton-card-phone-link" className="font-bold text-lake hover:underline">
              {LM.phone}
            </a>
            <span className="block mt-1">Call for availability, pricing and towing rules.</span>
          </InfoCard>
          <InfoCard icon={Info} title="Hours" testId="milton-card-hours">
            <span data-testid="milton-hours">
              {h ? `Daily ${h.open} – ${h.close}` : "Call for this season's hours."}
            </span>
            <span className="mt-1 block text-xs text-slate-500">
              Hours can shift with the season and the weather — calling ahead is always the safest bet.
            </span>
          </InfoCard>
        </div>
        <div className="mt-10">
          <Link
            to="/rental-requirements"
            data-testid="milton-requirements-link"
            className="inline-flex items-center gap-2 rounded-full bg-ember px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-ember-hover"
          >
            Review rental requirements
          </Link>
        </div>
      </Section>

      <WeatherBanner location="lake-milton" title="Lake Milton Marina weather" />
    </>
  );
}
